import get from 'dlv';
import { withFormik } from 'formik';
import {
  SentinelUser,
  toInternalUser,
} from '@src/config/default/modules/Authentication/components/types/user';

import {
  createNewsletterContact,
  deleteNewsletterContact,
} from '@modules/news/widgets/NewsletterWidget';
import withWidth from '@src/hocs/Width';
import { alertSuccess } from '@src/lib/alert';
import { NewsletterContact, NewsletterSubscriptionStatus } from '@src/meta/types/newsletter';
import UserResource from '@src/services/Identity/UserResource';
import { ILogout } from '@src/actions/auth';
import { fetchWithHandling } from '@src/utils/fetching';

import UserForm from './UserForm';

export type UserFormProps = {
  width?: number;
  configurableUser: SentinelUser;
  logout: () => Promise<ILogout>;
  updateUser: (user: SentinelUser) => void;
  changePasswordClick: () => void;
  openNewsletterModal: boolean;
  setOpenNewsletterModal: (b: boolean) => void;
  accountId?: string;
  disabled?: boolean;
};

export type UserFormValues = {
  newsletter_consent: boolean;
  initialNewsletterConsent: boolean;
  givenName: string;
  familyName: string;
  email: string;
  countryCode: string;
  contact?: NewsletterContact;
};

const UserFormContainer = withFormik<UserFormProps, UserFormValues>({
  validateOnChange: false,

  mapPropsToValues: ({ configurableUser: { givenName, familyName, email, countryCode } }) => {
    return {
      givenName,
      familyName,
      countryCode: countryCode ?? '',
      newsletter_consent: false,
      initialNewsletterConsent: false,
      email,
    };
  },
  handleSubmit: async (values, { props, setSubmitting, setErrors, setFieldValue }) => {
    setSubmitting(true);

    const currentUser: SentinelUser = { ...props.configurableUser };

    if (get(currentUser, 'organization.@id', '').includes('null')) {
      delete currentUser.organization;
    }
    if (values.initialNewsletterConsent !== values.newsletter_consent) {
      const status: NewsletterSubscriptionStatus = values.newsletter_consent
        ? 'SUBSCRIBED'
        : 'UNSUBSCRIBED';

      if (status === 'SUBSCRIBED') {
        const payload = {
          email: props.configurableUser.email,
          extra: { FIRSTNAME: values.givenName, LASTNAME: values.familyName },
          status,
        };
        await createNewsletterContact(payload as NewsletterContact);
        props.setOpenNewsletterModal(true);
      } else {
        await deleteNewsletterContact(props.configurableUser.email);
      }
      setFieldValue('initialNewsletterConsent', values.newsletter_consent);
    }

    const updatedUser = {
      ...currentUser,
      ...values,
      countryCode: values.countryCode.toUpperCase(),
      extra: { ...currentUser.extra },
      name: `${values.givenName} ${values.familyName}`,
      userId: props.configurableUser.id,
    };

    UserResource.update(updatedUser)
      .then(() => {
        setSubmitting(false);
        setErrors({});
        props.updateUser({ ...currentUser, ...values });
        fetchWithHandling(
          () => UserResource.get({ userId: updatedUser.id }),
          undefined,
          undefined,
          'Error loading user!',
        ).then(response => response && props.updateUser(toInternalUser(response)));
        alertSuccess('User successfully saved!');
      })
      .catch(() => {
        setErrors({ non_field_errors: ['Something went wrong'] } as any);
        setSubmitting(false);
      });
  },
})(UserForm);

export default withWidth(UserFormContainer);
